import type { ChangeLogEvent, ItemStatus, PublishState, TacticApplication } from './cockpit-data';

// KET-2764 truth-in-UI. `nothing` = the machine never acted here. `retired` =
// it acted and every application has since been retired, so nothing is in force
// now. Before this state existed, `retired` fell through to `unknown`, whose copy
// claims the publish journal could not be read -- a false statement about a row
// whose history reads perfectly well.
export type ItemAmazonState = PublishState | 'nothing' | 'retired';
export type ItemStatusSortKey = 'last_applied' | 'depth' | 'profit_yield' | 'orders';
export type ChangeLogSortKey = 'applied_at' | 'keyword' | 'market' | 'event_status';
export type TableSortDirection = 'asc' | 'desc';

export function displayTacticName(value: string): string {
  const cleaned = value.replace(/^W3\s+/i, '').trim();
  if (!cleaned.includes('_')) return cleaned;
  const humanized = cleaned.replace(/_/g, ' ').toLowerCase();
  return humanized ? humanized.charAt(0).toUpperCase() + humanized.slice(1) : cleaned;
}

export function itemAmazonState(row: ItemStatus): ItemAmazonState {
  if (!row.has_tactic) return 'nothing';
  const states = new Set(row.active_stack.map((application) => application.publish_state));
  if (states.has('published')) return 'published';
  if (states.has('reversed')) return 'reversed';
  if (states.has('unknown')) return 'unknown';
  return row.active_stack.length ? 'not_published' : 'retired';
}

export function itemAmazonApplication(row: ItemStatus): TacticApplication | null {
  const state = itemAmazonState(row);
  if (state === 'nothing' || state === 'retired') return null;
  return row.active_stack.find((application) => application.publish_state === state)
    ?? row.active_stack[0]
    ?? null;
}

export type ItemStatusFilters = {
  market: string;
  product: string;
  tactic: string;
  amazonState: string;
  search: string;
};

export function filterAndSortItemStatus(
  rows: ItemStatus[],
  filters: ItemStatusFilters,
  sortKey: ItemStatusSortKey,
  sortDir: TableSortDirection,
): ItemStatus[] {
  const needle = filters.search.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    if (filters.market && row.market !== filters.market) return false;
    if (filters.product && row.product !== filters.product) return false;
    if (filters.tactic && !row.active_stack.some((application) => application.tactic_name === filters.tactic)) return false;
    if (filters.amazonState && itemAmazonState(row) !== filters.amazonState) return false;
    if (needle && !row.keyword.toLowerCase().includes(needle)) return false;
    return true;
  });
  const direction = sortDir === 'asc' ? 1 : -1;
  return filtered.sort((left, right) => {
    const leftValue = itemSortValue(left, sortKey);
    const rightValue = itemSortValue(right, sortKey);
    if (leftValue === null && rightValue !== null) return 1;
    if (leftValue !== null && rightValue === null) return -1;
    if (leftValue !== rightValue) return compare(leftValue, rightValue) * direction;
    return left.market.localeCompare(right.market) || left.keyword.localeCompare(right.keyword);
  });
}

function itemSortValue(row: ItemStatus, key: ItemStatusSortKey): number | string | null {
  if (key === 'depth') return row.depth;
  if (key === 'profit_yield') return row.profit_yield;
  if (key === 'orders') return row.item_economics?.orders ?? null;
  return row.has_tactic ? row.last_applied : null;
}

export type ChangeLogFilters = {
  market: string;
  product: string;
  tactic: string;
  eventStatus: string;
  dateFrom: string;
  dateTo: string;
  item: string;
};

export function filterAndSortChangeLog(
  rows: ChangeLogEvent[],
  filters: ChangeLogFilters,
  sortKey: ChangeLogSortKey,
  sortDir: TableSortDirection,
): ChangeLogEvent[] {
  const filtered = rows.filter((row) => {
    if (filters.market && row.market !== filters.market) return false;
    if (filters.product && row.product !== filters.product) return false;
    if (filters.tactic && row.tactic_name !== filters.tactic) return false;
    if (filters.eventStatus && row.event_status !== filters.eventStatus) return false;
    if (filters.item && row.item_key !== filters.item) return false;
    const day = row.applied_at?.slice(0, 10) ?? '';
    if (filters.dateFrom && (!day || day < filters.dateFrom)) return false;
    if (filters.dateTo && (!day || day > filters.dateTo)) return false;
    return true;
  });
  const direction = sortDir === 'asc' ? 1 : -1;
  return filtered.sort((left, right) => {
    const leftValue = changeLogSortValue(left, sortKey);
    const rightValue = changeLogSortValue(right, sortKey);
    if (leftValue === null && rightValue !== null) return 1;
    if (leftValue !== null && rightValue === null) return -1;
    if (leftValue !== rightValue) return compare(leftValue, rightValue) * direction;
    return left.application_id.localeCompare(right.application_id);
  });
}

function changeLogSortValue(row: ChangeLogEvent, key: ChangeLogSortKey): string | null {
  return row[key];
}

function compare(left: number | string | null, right: number | string | null): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right));
}

export function itemStatusSortCaption(key: ItemStatusSortKey, direction: TableSortDirection): string {
  const label = {
    last_applied: 'last applied',
    depth: 'depth',
    profit_yield: 'Profit Yield',
    orders: 'orders',
  }[key];
  return `Sorted by ${label}, ${direction === 'asc' ? 'lowest first' : 'newest first'}.`.replace(
    'depth, newest first',
    'depth, highest first',
  ).replace('Profit Yield, newest first', 'Profit Yield, highest first')
    .replace('orders, newest first', 'orders, highest first')
    .replace('last applied, lowest first', 'last applied, oldest first');
}
